import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { formatCount } from '@/lib/numbers';
import { testIdPart } from '@/lib/testIds';

import {
  ACTIVITY,
  LATER_RELEASE,
  type ActivityEntry,
  type DashboardData,
} from './dashboardData';

interface CurrentActivityCardProps {
  /** Absent while loading. */
  activity?: DashboardData['activity'];
  /**
   * The entries to list; the page's own by default. The page never passes
   * it: while no section is built, every `ACTIVITY` entry is unavailable, and
   * this is how the tests draw an available one (a link with its count) until
   * the first section lands and `ACTIVITY` holds one itself.
   */
  entries?: readonly ActivityEntry[];
}

/**
 * What is live right now, each count a link to the section that manages it.
 *
 * An entry whose section is not built yet says so in place of its count and
 * is not a link, nor a stop on the way through the page: a link to a
 * placeholder section is a dead end (P7, P21).
 */
export function CurrentActivityCard({
  activity,
  entries = ACTIVITY,
}: CurrentActivityCardProps) {
  const headingId = useId();

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      data-testid="dashboard-current-activity-card"
      sx={{ p: 2, minWidth: 0 }}
    >
      <Typography id={headingId} variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        Current Activity
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Published and scheduled work
      </Typography>

      <List data-testid="dashboard-current-activity-list" sx={{ mt: 1 }}>
        {entries.map((entry) =>
          entry.available ? (
            <ListItem key={entry.id} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={entry.to}
                data-testid={`dashboard-current-activity-${testIdPart(entry.id)}-link`}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText primary={entry.label} />
                <Typography
                  component="span"
                  data-testid={`dashboard-current-activity-${testIdPart(entry.id)}-count`}
                  sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}
                >
                  {/* A placeholder the width of a count, so the row keeps its
                    size and no number is claimed before it is known. */}
                  {activity ? (
                    formatCount(activity[entry.id])
                  ) : (
                    <Skeleton width={32} sx={{ ml: 'auto' }} />
                  )}
                </Typography>
                {/* Says at rest that the row goes somewhere; the text alone
                  reads as a label and a count. */}
                <ChevronRightIcon
                  fontSize="small"
                  aria-hidden="true"
                  sx={{ color: 'text.secondary', ml: 1 }}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            <ListItem key={entry.id} sx={{ px: 2, py: 1 }}>
              <ListItemText primary={entry.label} />
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                data-testid={`dashboard-current-activity-${testIdPart(entry.id)}-later-message`}
                sx={{ textAlign: 'right', ml: 2 }}
              >
                {LATER_RELEASE}
              </Typography>
            </ListItem>
          ),
        )}
      </List>
    </Paper>
  );
}
